import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Autocomplete,
  Chip,
  Alert,
  Tab,
  Tabs
} from '@mui/material';
import IndicatorHelp from '../components/IndicatorHelp';
import ExamplesPanel from '../components/ExamplesPanel';
import EnhancedResultsTable from '../components/EnhancedResultsTable';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ScreenerPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [queryValid, setQueryValid] = useState(true);
  const [indicators, setIndicators] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [stockLists, setStockLists] = useState([]);
  const [selectedStockList, setSelectedStockList] = useState(null);
  const [activeTab, setActiveTab] = useState('editor');

  const updateSuggestions = useCallback((currentQuery) => {
    if (!indicators) return;

    const words = currentQuery.trim().split(' ');
    const lastWord = words[words.length - 1] || '';

    const allIndicators = Object.values(indicators.indicators)
      .filter(category => typeof category === 'object')
      .reduce((acc, category) => {
        return [...acc, ...Object.keys(category)];
      }, []);

    setSuggestions(allIndicators.filter(ind =>
      ind.toLowerCase().startsWith(lastWord.toLowerCase())
    ));
  }, [indicators]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [indRes, listsRes] = await Promise.all([
          fetch('/api/v1/indicators'),
          fetch('/api/v1/stock-lists')
        ]);

        if (!indRes.ok || !listsRes.ok) {
          throw new Error('Failed to fetch indicator data');
        }

        const [indData, listsData] = await Promise.all([
          indRes.json(),
          listsRes.json()
        ]);

        setIndicators(indData);
        setStockLists(listsData.lists);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load indicators. Please refresh the page.');
      }
    };

    fetchData();
  }, []);

  const handleQueryChange = (event, value) => {
    const newQuery = value || event?.target?.value || '';
    setQuery(newQuery);
    updateSuggestions(newQuery);
  };

  const validateQuery = (queryText) => {
    if (!queryText.trim()) {
      setError('Query cannot be empty');
      setQueryValid(false);
      return false;
    }

    const trimmedQuery = queryText.trim();

    if (trimmedQuery.split(' ').length < 4) {
      setError('Query must include timeframe, indicator, operator, and value');
      setQueryValid(false);
      return false;
    }

    setError(null);
    setQueryValid(true);
    return true;
  };

  const handleExampleClick = (exampleQuery) => {
    setQuery(exampleQuery);
    updateSuggestions(exampleQuery);
    setActiveTab('editor');
  };

  const handleSubmit = async () => {
    if (!validateQuery(query)) return;
    setError(null);
    setResults(null);

    try {
      const requestData = {
        query: query,
        is_natural_language: false,
        max_results: 50,
        stock_list: selectedStockList ? selectedStockList.id : 'nifty50',
        debug: true
      };

      console.log('Sending request:', requestData);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const response = await fetch('/api/v1/screen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      const data = await response.json();
      clearTimeout(timeout);
      console.log('API Response:', data);

      if (!response.ok) {
        throw new Error(`API Error: ${data.detail || 'Failed to execute query'}`);
      }

      if (data.total_matches === 0) {
        setError('No stocks found matching the criteria. Try adjusting your values.');
      } else {
        setError(null);
      }

      setResults(data);
    } catch (error) {
      console.error('Error:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(error.message || 'Failed to execute query. Please check your syntax.');
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Stock Screener
        </Typography>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Query Builder" value="editor" />
          <Tab label="Indicators" value="indicators" />
          <Tab label="Examples" value="examples" />
          <Tab label="Documentation" value="docs" />
        </Tabs>
      </Box>

      {activeTab === 'editor' && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Stock Universe
              </Typography>
              <Autocomplete
                fullWidth
                value={selectedStockList}
                onChange={(event, newValue) => setSelectedStockList(newValue)}
                options={stockLists}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select stock list (default: NIFTY50)"
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Query Builder
              </Typography>
              <Autocomplete
                freeSolo
                fullWidth
                value={query}
                onChange={handleQueryChange}
                onInputChange={(event, value) => handleQueryChange(event, value)}
                options={suggestions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    multiline
                    rows={3}
                    label="Enter Query"
                    placeholder="e.g., 1D RSI(14) > 70"
                    sx={{ mb: 2 }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option}
                    {indicators?.indicators[option] && (
                      <Chip
                        size="small"
                        label={`Params: ${indicators.indicators[option].join(', ')}`}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                )}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
                <SyntaxHighlighter
                  language="sql"
                  style={tomorrow}
                  customStyle={{ fontSize: '0.9em' }}
                >
                  {query || '# Example: 1D RSI(14) > 70'}
                </SyntaxHighlighter>
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!query.trim() || !queryValid}
              >
                Screen Stocks
              </Button>
            </Box>
          </Paper>

          {results && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Results ({results.total_matches} matches found)
              </Typography>

              <EnhancedResultsTable results={results} />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Screened: {results.total_screened} |
                  Execution Time: {results.execution_time_ms.toFixed(2)}ms
                </Typography>
              </Box>
            </Paper>
          )}
        </>
      )}

      {activeTab === 'indicators' && indicators && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Technical Indicators
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Click on any indicator to add it to your query
            </Typography>
          </Box>

          {['trend', 'momentum', 'volatility', 'volume', 'oscillators'].map(category => (
            indicators.indicators[category] && (
              <Box key={category} sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(indicators.indicators[category]).map(([name, info]) => (
                    <Chip
                      key={name}
                      label={`${name}(${info.params.join(',')})`}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const indicatorText = `1D ${name}(${info.params.join(',')})`;
                        setQuery((prevQuery) => {
                          const baseQuery = prevQuery.trim();
                          return baseQuery ? `${baseQuery} ${indicatorText}` : indicatorText;
                        });
                        setActiveTab('editor');
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )
          ))}
        </Paper>
      )}

      {activeTab === 'examples' && (
        <Paper sx={{ p: 3 }}>
          <ExamplesPanel onExampleClick={handleExampleClick} />
        </Paper>
      )}

      {activeTab === 'docs' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Indicator Documentation
          </Typography>
          <IndicatorHelp />
        </Paper>
      )}
    </Container>
  );
};

export default ScreenerPage;